import React, { useMemo } from "react";
import { Autocomplete, Box, Grid, TextField } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  CreateMaintenanceRecordRequest,
  MaintenanceRecordDto,
  VehicleMaintenanceDto,
  useCreateMaintenanceRecordMutation,
  useUpdateMaintenanceRecordMutation,
} from "../../../api/maintenanceApi";
import { useGetShopsQuery } from "../../../api/shopsApi";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";
import {
  GenericNumberField,
  GenericDateField,
  GenericTextField,
} from "../../../components/common/FormFields";
import InvoiceGalleryPicker from "./InvoiceGalleryPicker";

interface MaintenanceRecordDialogProps {
  open: boolean;
  onClose: () => void;
  vehicles: VehicleMaintenanceDto[];
  preselectedVehicleId?: number | null;
  editRecord?: MaintenanceRecordDto | null;
}

const validationSchema = Yup.object({
  vehicleId: Yup.number().required("Vehicle is required").min(1, "Vehicle is required"),
  date: Yup.string().required("Date is required"),
  description: Yup.string().required("Description is required"),
});

const toDateInputValue = (date: Date | string): string => {
  if (typeof date === "string") {
    return date.split("T")[0];
  }
  return date.toISOString().split("T")[0];
};

const MaintenanceRecordDialog: React.FC<MaintenanceRecordDialogProps> = ({
  open,
  onClose,
  vehicles,
  preselectedVehicleId,
  editRecord,
}) => {
  const [createRecord, { isLoading: isCreating }] = useCreateMaintenanceRecordMutation();
  const [updateRecord, { isLoading: isUpdating }] = useUpdateMaintenanceRecordMutation();
  const { data: shops = [] } = useGetShopsQuery(undefined);
  const activeShops = useMemo(() => shops.filter((s) => s.isActive), [shops]);

  const isEditMode = !!editRecord;
  const isLoading = isCreating || isUpdating;

  const preselected = vehicles.find(
    (v) => v.vehicleId === (editRecord?.vehicleId ?? preselectedVehicleId)
  );

  const initialValues: CreateMaintenanceRecordRequest & { invoiceIds: number[] } = {
    vehicleId: editRecord?.vehicleId ?? preselectedVehicleId ?? 0,
    shopId: editRecord?.shopId ?? null,
    cost: editRecord?.cost ?? null,
    date: editRecord ? toDateInputValue(editRecord.date) : toDateInputValue(new Date()),
    mileage: editRecord?.mileage ?? preselected?.currentOdometer ?? null,
    description: editRecord?.description ?? "",
    invoiceIds: editRecord?.invoices?.map((i) => i.id) ?? [],
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        try {
          if (isEditMode) {
            await updateRecord({
              recordId: editRecord!.id,
              data: {
                vehicleId: values.vehicleId,
                shopId: values.shopId,
                cost: values.cost,
                date: values.date,
                mileage: values.mileage,
                description: values.description,
                invoiceIds: values.invoiceIds.length > 0 ? values.invoiceIds : null,
              },
            }).unwrap();
          } else {
            await createRecord({
              ...values,
              invoiceIds: values.invoiceIds.length > 0 ? values.invoiceIds : null,
            }).unwrap();
          }
          resetForm();
          onClose();
        } catch (err) {
          console.error(
            `Failed to ${isEditMode ? "update" : "create"} maintenance record:`,
            err
          );
        }
      }}
    >
      {({ handleSubmit: formikSubmit, values, setFieldValue, errors, touched, resetForm }) => (
        <FormDialog
          open={open}
          title={isEditMode ? "Edit Maintenance Record" : "Add Maintenance Record"}
          onClose={() => {
            resetForm();
            onClose();
          }}
          onSubmit={formikSubmit}
          isLoading={isLoading}
          maxWidth="md"
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={vehicles}
                getOptionLabel={(v) => v.vehicleName}
                isOptionEqualToValue={(a, b) => a.vehicleId === b.vehicleId}
                value={vehicles.find((v) => v.vehicleId === values.vehicleId) ?? null}
                onChange={(_, selected) => {
                  const vid = selected?.vehicleId ?? 0;
                  setFieldValue("vehicleId", vid);
                  setFieldValue("invoiceIds", []);
                  if (selected?.currentOdometer != null) {
                    setFieldValue("mileage", selected.currentOdometer);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehicle *"
                    placeholder="Type to search..."
                    error={touched.vehicleId && Boolean(errors.vehicleId)}
                    helperText={touched.vehicleId && errors.vehicleId}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={activeShops}
                getOptionLabel={(s) => s.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={activeShops.find((s) => s.id === values.shopId) ?? null}
                onChange={(_, selected) => setFieldValue("shopId", selected?.id ?? null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Shop"
                    placeholder="Type to search..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <GenericNumberField name="cost" label="Cost ($)" min={0} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <GenericDateField name="date" label="Date *" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <GenericNumberField name="mileage" label="Mileage (miles)" min={0} />
            </Grid>
            <Grid item xs={12}>
              <GenericTextField
                name="description"
                label="Description *"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <InvoiceGalleryPicker
              selectedIds={values.invoiceIds}
              onChange={(ids) => setFieldValue("invoiceIds", ids)}
              vehicleId={values.vehicleId || null}
              knownInvoices={editRecord?.invoices}
            />
          </Box>
        </FormDialog>
      )}
    </Formik>
  );
};

export default MaintenanceRecordDialog;
